import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, MoreHorizontal, Eye, Edit, Trash2, FileText, Clock, CheckCircle, XCircle } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import ProcurementService from '@/api/procurement.service';
import { canCreate, canEdit, canDelete, canView } from '@/lib/permissions';

const SupplierQuotations: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [quotations, setQuotations] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [suppliers, setSuppliers] = useState<any[]>([]);

  const businessId = parseInt(localStorage.getItem('businessId') || '1');

  useEffect(() => {
    fetchQuotations();
    fetchSuppliers();
  }, []);

  const fetchQuotations = async () => {
    try {
      setIsLoading(true);
      const response = await ProcurementService.getSupplierQuotations(businessId);
      
      if (response.data?.success) {
        setQuotations(response.data.data || []);
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load quotations",
        });
      }
    } catch (error: any) {
      console.error('Error fetching quotations:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load quotations",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await ProcurementService.getSuppliers(businessId);
      if (response.data?.success) {
        setSuppliers(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const handleDeleteQuotation = async (id: number) => {
    if (!canDelete('quotations')) {
      toast({
        variant: "destructive",
        title: "Permission Denied",
        description: "You don't have permission to delete quotations",
      });
      return;
    }

    try {
      const response = await ProcurementService.deleteSupplierQuotation(businessId, id);
      
      if (response.data?.success) {
        toast({
          title: "Success",
          description: "Quotation deleted successfully",
        });
        fetchQuotations();
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to delete quotation",
        });
      }
    } catch (error: any) {
      console.error('Error deleting quotation:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete quotation",
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      draft: { variant: 'secondary' as const, label: 'Draft' },
      sent: { variant: 'outline' as const, label: 'Sent' },
      received: { variant: 'default' as const, label: 'Received' },
      approved: { variant: 'default' as const, label: 'Approved' },
      rejected: { variant: 'destructive' as const, label: 'Rejected' },
      expired: { variant: 'secondary' as const, label: 'Expired' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || { variant: 'secondary' as const, label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const filteredQuotations = quotations.filter(quotation => {
    const matchesSearch = quotation.quotationNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         quotation.supplier?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         quotation.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || quotation.status === statusFilter;
    const matchesSupplier = supplierFilter === 'all' || quotation.supplierId?.toString() === supplierFilter;
    
    return matchesSearch && matchesStatus && matchesSupplier;
  });

  const getQuotationStats = () => {
    const total = quotations.length;
    const pending = quotations.filter(q => q.status === 'sent').length;
    const approved = quotations.filter(q => q.status === 'approved').length;
    const totalValue = quotations.reduce((sum, q) => sum + (q.totalAmount || 0), 0);

    return { total, pending, approved, totalValue };
  };

  const stats = getQuotationStats();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          <span>Loading quotations...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Supplier Quotations</h1>
          <p className="text-muted-foreground">
            Manage quotation requests and supplier responses
          </p>
        </div>
        {canCreate('quotations') && (
          <Button 
            onClick={() => navigate('/procurement/quotations/create')}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Request Quotation
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Quotations</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Response</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.pending}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Approved</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.approved}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Value</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats.totalValue)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filter Quotations</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search quotations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="received">Received</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>

            <Select value={supplierFilter} onValueChange={setSupplierFilter}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="Filter by supplier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Suppliers</SelectItem>
                {suppliers.map((supplier) => (
                  <SelectItem key={supplier.id} value={supplier.id.toString()}>
                    {supplier.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Quotations Table */}
      <Card>
        <CardHeader>
          <CardTitle>Quotations</CardTitle>
          <CardDescription>
            {filteredQuotations.length} of {quotations.length} quotations
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredQuotations.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-2 text-sm font-semibold">No quotations found</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {quotations.length === 0 
                  ? "Get started by requesting your first quotation."
                  : "Try adjusting your search or filter criteria."
                }
              </p>
              {canCreate('quotations') && quotations.length === 0 && (
                <Button 
                  onClick={() => navigate('/procurement/quotations/create')}
                  className="mt-4"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Request Quotation
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Quotation #</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Request Date</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Total Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[50px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredQuotations.map((quotation) => (
                  <TableRow key={quotation.id}>
                    <TableCell className="font-medium">
                      {quotation.quotationNumber}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{quotation.supplier?.name}</p>
                        <p className="text-sm text-muted-foreground">{quotation.supplier?.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {new Date(quotation.requestDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {quotation.dueDate ? new Date(quotation.dueDate).toLocaleDateString() : 'N/A'}
                    </TableCell>
                    <TableCell>{quotation.itemCount || 0}</TableCell>
                    <TableCell>
                      {quotation.totalAmount ? formatCurrency(quotation.totalAmount) : 'Pending'}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(quotation.status)}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {canView('quotations') && (
                            <DropdownMenuItem onClick={() => navigate(`/procurement/quotations/${quotation.id}`)}>
                              <Eye className="mr-2 h-4 w-4" />
                              View Details
                            </DropdownMenuItem>
                          )}
                          {canEdit('quotations') && quotation.status === 'draft' && (
                            <DropdownMenuItem onClick={() => navigate(`/procurement/quotations/${quotation.id}/edit`)}>
                              <Edit className="mr-2 h-4 w-4" />
                              Edit
                            </DropdownMenuItem>
                          )}
                          {quotation.status === 'received' && (
                            <DropdownMenuItem onClick={() => navigate(`/procurement/quotations/compare?ids=${quotation.id}`)}>
                              <FileText className="mr-2 h-4 w-4" />
                              Compare
                            </DropdownMenuItem>
                          )}
                          {canDelete('quotations') && quotation.status === 'draft' && (
                            <DropdownMenuItem 
                              onClick={() => handleDeleteQuotation(quotation.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SupplierQuotations; 